import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { FormattedMessage } from 'react-intl';
import { fromJS } from 'immutable';
import * as _ from 'lodash';
import validator from 'validator';
import ReactResizeDetector from 'react-resize-detector';
import { removeTimeZoneFromDate } from 'utils/dateUtil';
import injectReducer from 'utils/injectReducer';
import { makeSelectStepSnowSports, makeSelectStepSnowSportsErrors } from './selectors';
import reducer from './reducer';
import messages from './messages';

import { Button } from '../components/Button';
import PageSectionHeader from '../components/PageSectionHeader';
import FlexBox from '../../../components/FlexBox';
import P from '../../../components/P';

import { handleFormDataChange, nextSlide, resetFormData } from './actions';
import DatePicker from '../components/DatePicker/index';
import Textbox from '../components/Textbox';
import TextArea from '../components/TextArea';
import OptionsContainer from '../components/OptionsContainer';
import { REPORT_TO } from './constants';
import { AdditionalList } from './components/Additional/index';
import { CancellationExpensesList } from './components/CancellationExpenses/index';
import { AdditionalExpensesList } from './components/AdditionalExpenses/index';
import {
  ErrorMessage,
  Validate,
  ValidateGroup,
} from '../../../components/Validation';
import { required } from '../../../utils/validation/validator';
import { saveFormData } from '../actions';
import groupOption from '../../../components/Validation/groupOption';
import { makeSelectCurrentStep } from '../../Wizard/selectors';
import { makeSelectProgressBarStatus } from '../../ProgressBar/selectors';
import AsyncButton from '../../../components/AsyncButton/index';
import StepSnowSportsShared from './components/StepSnowSportsShared';

export class StepSnowSports extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      validationCounter: 0,
      errors: [],
    };
    this.validatorGroup = new ValidateGroup();
    this.handleNext = this.handleNext.bind(this);
    this.checkFormError = this.checkFormError.bind(this);
    this.onResize = this.onResize.bind(this);

    this.saveButtonId = _.uniqueId();
    this.clickedItemId = '';
    this.nextButton = null;
  }

  componentWillReceiveProps(nextProps) {
    const { currentStep, step } = this.props;
    if (currentStep !== nextProps.currentStep && currentStep === step) {
      this.checkFormError();
    }

    if (this.props.isReadOnly && nextProps.errors !== this.props.errors) {
      this.state.validationCounter += 1;
      this.validatorGroup.checkValidation();
    }

    if (!nextProps.loading) {
      this.clickedItemId = '';
    }
  }

  onResize() {
    const { adjust } = this.props;
    if (adjust) {
      adjust();
    }
  }

  onChange = (propertyName) => (event) => {
    if (event === null) {
      this.props.onFormDataChange(propertyName, null);
      return;
    }

    if (event.constructor.name === 'Moment') {
      this.props.onFormDataChange(propertyName, removeTimeZoneFromDate(event.toDate().toString()));
    } else {
      const target = event.target;
      const value = target.type === 'checkbox' ? target.checked : target.value;
      if (target.type === 'radio') {
        this.props.adjust();
      }
      this.props.onFormDataChange(propertyName, value);
    }
    // this.props.makeDirty(9);
  };

  onAdditionalItemsChange = (property) => (data) => {
    this.props.onFormDataChange(`form.${property}`, fromJS(data));
    const { adjust } = this.props;
    if (adjust) {
      adjust();
    }
  };

  onCancellationExpensesChange = (property) => (data) => {
    this.props.onFormDataChange(`form.${property}`, fromJS(data));
    const { adjust } = this.props;
    if (adjust) {
      adjust();
    }
  };

  onAdditionalExpensesChange = (property) => (data) => {
    this.props.onFormDataChange(`form.${property}`, fromJS(data));
    const { adjust } = this.props;
    if (adjust) {
      adjust();
    }
  };

  onInputComponentChange = (propertyName) => (event) => {
    if (event.target.checked) {
      const selectedValue = this.getBenefitOption();

      this.props.adjust();

      if (!!selectedValue && selectedValue.key !== propertyName) {
        this.props.onFormDataChange(`form.${selectedValue.key}`, 'false');
      }
    }
    this.onChange(`${propertyName}`)(event);
    // this.props.makeDirty(5);
  };

  getBenefitOption = () => {
    const {
      areYouClaimingForSnowSportEquipment,
      areYouClaimingForSnowEquipmentReplacementHire,
    } = this.props.form;

    const benefitOptions = [
      {
        key: 'areYouClaimingForSnowSportEquipment',
        value: areYouClaimingForSnowSportEquipment,
      },
      {
        key: 'areYouClaimingForSnowEquipmentReplacementHire',
        value: areYouClaimingForSnowEquipmentReplacementHire,
      },
    ];

    const result = benefitOptions.find(
      (benefitOption) => benefitOption.value === 'true'
    );

    return result;
  };

  getValidationErrors() {
    const {
      areYouClaimingForSnowSportEquipment,
      areYouClaimingForSnowEquipmentReplacementHire,
      areYouClaimingForSnowPrepaidExpenses,
      areYouClaimingForSnowResortClosure,
      cancellationExpenses,
      additionalExpenses,
      additionalItems,
    } = this.props.form;
    const errors = [];

    if (
      areYouClaimingForSnowSportEquipment === 'true' ||
      areYouClaimingForSnowEquipmentReplacementHire === 'true'
    ) {
      if (this.validateTableField(additionalItems)) {
        errors.push({
          field: 'additionalItems',
          message: 'Expense table fields are required fields.',
        });
      }
    }

    if (
      areYouClaimingForSnowPrepaidExpenses === 'true' ||
      areYouClaimingForSnowResortClosure === 'true'
    ) {
      if (this.validateTableField(cancellationExpenses)) {
        errors.push({
          field: 'cancellationExpenses',
          message: 'Cancellation Expenses table fields are required fields.',
        });
      }
      if (this.validateTableField(additionalExpenses)) {
        errors.push({
          field: 'additionalExpenses',
          message: 'Additional Expenses table fields are required fields.',
        });
      }
    }

    return errors;
  }

  handleKeyDown = (e) => {
    if (e.shiftKey && e.key === 'Tab') {
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
    }
  };

  handleSaveButtonClick = (event) => {
    const target = event.currentTarget || event.srcElement;
    this.clickedItemId = target.id;
    this.props.onFormDataSave();
    this.nextButton.focus();
  };

  showSpinnerForElement = (elementId) => this.clickedItemId === elementId;

  checkFormError() {
    const valid = this.validatorGroup.checkValidation();
    this.props.onNext(!valid);
    if (!valid) {
      this.props.makeError(this.props.step);
      const counter = this.state.validationCounter + 1;
      this.setState({ validationCounter: counter });
    } else {
      this.props.makeValid(this.props.step);
    }
  }

  handleChange = (propertyName) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    this.props.onFormDataChange(propertyName, value);
    // this.props.makeDirty(9);
  };

  handleNext = () => {
    const { submitted } = this.props;
    if (submitted) {
      this.props.next();
      return;
    }

    // const errors = this.validatorGroup.getErrors();
    const valid = this.validatorGroup.checkValidation();

    this.props.onNext(!valid);
    if (!valid) {
      this.props.makeError(this.props.step);
      const counter = this.state.validationCounter + 1;
      this.setState({ validationCounter: counter });
    } else {
      this.props.makeValid(this.props.step);
    }

    this.props.onFormDataSave();
    this.props.next();
  };

  handleFormDataSave = () => {
    this.props.onFormDataSave();
  };

  validateTableField(groups) {
    const invalid = _.some(groups, (fields) =>
      _.some(fields, (value) => {
        const newValue = value ? value.toString() : '';
        return validator.isEmpty(newValue);
      })
    );
    return invalid;
  }

  handleDayChange = (propertyName) => (day) => {
    const localDate = removeTimeZoneFromDate(day);
    this.props.onFormDataChange(propertyName, localDate);
    // this.props.makeDirty(9);
  };

  appendIds = (array) =>
    array.map((item) => {
      let newItem = item; // eslint-disable-line
      if (!item.id) {
        newItem.id = new Date().getTime().toString();
      }
      return newItem;
    });

  PROPERTY = {
    SNOW_SPORT_EQUIPMENT: 'snowSportEquipment',
    SNOW_EQUIPMENT_REPLACEMENT_HIRE: 'snowEquipmentReplacementHire',
  };

  summaryFormValue() {
    return _.mapValues(this.props.form, (prop) => prop || 'N/A');
  }

  renderClaimNoteForSnowSportEquipment() {
    return (
      <div className="row">
        <div className="form-group col-12">
          <P style={{ fontWeight: 'bold' }}>
            <FormattedMessage {...messages.toAssistYourClaim} />
          </P>
          <ul style={{ paddingLeft: 20 }}>
            <li>
              <FormattedMessage {...messages.toAssistYourClaimDetail1} />
            </li>
            <li>
              <FormattedMessage {...messages.toAssistYourClaimDetail2} />
            </li>
          </ul>
          <P style={{ fontWeight: 'bold' }}>
            <FormattedMessage {...messages.document} />
          </P>
          <StepSnowSportsShared />
        </div>
        <div className="form-group col-12">
          <P style={{ fontWeight: 'bold' }}>
            <FormattedMessage {...messages.provideExplanation} />
          </P>
        </div>
      </div>
    );
  }

  renderClaimNoteForSnowRepaidExpenses() {
    return (
      <div className="row">
        <div className="form-group col-12">
          <P style={{ fontWeight: 'bold' }}>
            <FormattedMessage {...messages.toAssistYourClaim} />
          </P>
          <ul style={{ paddingLeft: 20 }}>
            <li>
              <FormattedMessage {...messages.toAssistYourClaimDetail1} />
            </li>
            <li>
              <FormattedMessage {...messages.toAssistYourClaimDetail2} />
            </li>
          </ul>
          <P style={{ fontWeight: 'bold' }}>
            <FormattedMessage {...messages.document} />
          </P>
          <ol>
            <li>
              <FormattedMessage {...messages.document7} />
            </li>
            <li>
              <FormattedMessage {...messages.document8} />
            </li>
            <li>
              <FormattedMessage {...messages.document9} />
            </li>
          </ol>
        </div>
        <div className="form-group col-12">
          <P style={{ fontWeight: 'bold' }}>
            <FormattedMessage {...messages.provideExplanation} />
          </P>
        </div>
      </div>
    );
  }

  renderClaimNoteForSnowResortClosure() {
    return (
      <div className="row">
        <div className="form-group col-12">
          <P style={{ fontWeight: 'bold' }}>
            <FormattedMessage {...messages.toAssistYourClaim} />
          </P>
          <ul style={{ paddingLeft: 20 }}>
            <li>
              <FormattedMessage {...messages.toAssistYourClaimDetail1} />
            </li>
            <li>
              <FormattedMessage {...messages.toAssistYourClaimDetail2} />
            </li>
          </ul>
          <P style={{ fontWeight: 'bold' }}>
            <FormattedMessage {...messages.cancellationExpenses} />
          </P>
          <ol>
            <li>
              <FormattedMessage {...messages.document10} />
            </li>
            <li>
              <FormattedMessage {...messages.document11} />
            </li>
          </ol>
        </div>
        <div className="form-group col-12">
          <P style={{ fontWeight: 'bold' }}>
            <FormattedMessage {...messages.provideExplanation} />
          </P>
        </div>
      </div>
    );
  }

  renderSnowSportEquipmentForm() {
    const { areYouClaimingForSnowSportEquipment } = this.props.form;
    const {
      howItHappened,
      whereYouWereInRelationToTheItem,
      additional,
      claimingFor,
      whoDidYouReportThisTo,
      otherDescription,
      reportReferenceNumber,
    } = this.props.form.snowSportEquipment;
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;

    if (areYouClaimingForSnowSportEquipment === 'true') {
      return (
        <div className="mb-4">
          {this.renderClaimNoteForSnowSportEquipment()}
          <div className="row">
            <div className="col-12">
              <P style={{ fontStyle: 'bold' }}>
                <FormattedMessage
                  {...messages.ifYourSnowSportEquipmentsWasLostStolenOrDamagedPleaseTellUs}
                />
              </P>
            </div>
          </div>

          <div className="row">
            <div className="form-group col-12">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="howItHappened"
                validators={[required]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={howItHappened}
                fieldName={messages.howItHappened}
              >
                <TextArea
                  disabled={isReadOnly}
                  name="howItHappened"
                  type="text"
                  onChange={this.handleChange(
                    'form.snowSportEquipment.howItHappened'
                  )}
                  value={howItHappened}
                  isSummary={isSummary}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.howItHappened}
                  />
                </ErrorMessage>
              </Validate>
            </div>
          </div>

          <div className="row">
            <div className="form-group col-12">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="whereYouWereInRelationToTheItem"
                validators={[required]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={whereYouWereInRelationToTheItem}
                fieldName={messages.whereYouWereInRelationToTheItem}
              >
                <TextArea
                  disabled={isReadOnly}
                  name="whereYouWereInRelationToTheItem"
                  type="text"
                  onChange={this.handleChange(
                    'form.snowSportEquipment.whereYouWereInRelationToTheItem'
                  )}
                  value={whereYouWereInRelationToTheItem}
                  isSummary={isSummary}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.whereYouWereInRelationToTheItem}
                  />
                </ErrorMessage>
              </Validate>
            </div>
          </div>

          <div className="row">
            <div className="form-group col-12">
              <PageSectionHeader>
                <FormattedMessage {...messages.anyThingAdditionalToAdd} />
              </PageSectionHeader>
              <TextArea
                disabled={isReadOnly}
                name="additional"
                type="text"
                onChange={this.handleChange(
                  'form.snowSportEquipment.additional'
                )}
                value={additional}
                isSummary={isSummary}
              />
            </div>
          </div>

          <div className="row">
            <div className="form-group col-12">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="areYouClaimingFor"
                validators={[required]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={groupOption(claimingFor)}
                fieldName={messages.areYouClaimingFor}
              >
                <FlexBox direction="column">
                  <OptionsContainer>
                    <input
                      disabled={isReadOnly}
                      type="checkbox"
                      name="loss"
                      checked={claimingFor.loss === true}
                      onChange={this.onChange(
                        'form.snowSportEquipment.claimingFor.loss'
                      )}
                    />
                    <span>
                      <FormattedMessage {...messages.loss} />
                    </span>
                  </OptionsContainer>
                  <OptionsContainer>
                    <input
                      disabled={isReadOnly}
                      type="checkbox"
                      name="theft"
                      checked={claimingFor.theft === true}
                      onChange={this.onChange(
                        'form.snowSportEquipment.claimingFor.theft'
                      )}
                    />
                    <span>
                      <FormattedMessage {...messages.theft} />
                    </span>
                  </OptionsContainer>
                  <OptionsContainer>
                    <input
                      disabled={isReadOnly}
                      type="checkbox"
                      name="damage"
                      checked={claimingFor.damage === true}
                      onChange={this.onChange(
                        'form.snowSportEquipment.claimingFor.damage'
                      )}
                    />
                    <span>
                      <FormattedMessage {...messages.damage} />
                    </span>
                  </OptionsContainer>
                  <OptionsContainer>
                    <input
                      disabled={isReadOnly}
                      type="checkbox"
                      name="delay"
                      checked={claimingFor.delay === true}
                      onChange={this.onChange(
                        'form.snowSportEquipment.claimingFor.delay'
                      )}
                    />
                    <span>
                      <FormattedMessage {...messages.delay} />
                    </span>
                  </OptionsContainer>
                </FlexBox>
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.areYouClaimingFor}
                  />
                </ErrorMessage>
              </Validate>
            </div>
          </div>

          <div className="row">
            <div className="form-group col-md-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="whoDidYouReportThisTo"
                validators={[required]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={whoDidYouReportThisTo}
                fieldName={messages.whoDidYouReportThisTo}
              >
                <FlexBox justifyContent="space-between">
                  <FlexBox direction="column">
                    <OptionsContainer>
                      <input
                        disabled={isReadOnly}
                        type="radio"
                        checked={whoDidYouReportThisTo === REPORT_TO.POLICE}
                        onChange={this.onChange(
                          'form.snowSportEquipment.whoDidYouReportThisTo'
                        )}
                        value={REPORT_TO.POLICE}
                      />
                      <FormattedMessage {...messages.police} />
                    </OptionsContainer>
                    <OptionsContainer>
                      <input
                        disabled={isReadOnly}
                        type="radio"
                        checked={
                          whoDidYouReportThisTo === REPORT_TO.HOTEL_MANAGEMENT
                        }
                        onChange={this.onChange(
                          'form.snowSportEquipment.whoDidYouReportThisTo'
                        )}
                        value={REPORT_TO.HOTEL_MANAGEMENT}
                      />
                      <FormattedMessage {...messages.hotelManagement} />
                    </OptionsContainer>
                    <OptionsContainer>
                      <input
                        disabled={isReadOnly}
                        type="radio"
                        checked={
                          whoDidYouReportThisTo === REPORT_TO.CRUISE_SHIP
                        }
                        onChange={this.onChange(
                          'form.snowSportEquipment.whoDidYouReportThisTo'
                        )}
                        value={REPORT_TO.CRUISE_SHIP}
                      />
                      <FormattedMessage {...messages.cruiseShip} />
                    </OptionsContainer>
                    <OptionsContainer>
                      <input
                        disabled={isReadOnly}
                        type="radio"
                        checked={whoDidYouReportThisTo === REPORT_TO.AIRLINE}
                        onChange={this.onChange(
                          'form.snowSportEquipment.whoDidYouReportThisTo'
                        )}
                        value={REPORT_TO.AIRLINE}
                      />
                      <FormattedMessage {...messages.airline} />
                    </OptionsContainer>
                  </FlexBox>
                  <FlexBox direction="column">
                    <OptionsContainer>
                      <input
                        disabled={isReadOnly}
                        type="radio"
                        checked={whoDidYouReportThisTo === REPORT_TO.TOUR_GUIDE}
                        onChange={this.onChange(
                          'form.snowSportEquipment.whoDidYouReportThisTo'
                        )}
                        value={REPORT_TO.TOUR_GUIDE}
                      />
                      <FormattedMessage {...messages.tourGuide} />
                    </OptionsContainer>
                    <OptionsContainer>
                      <input
                        disabled={isReadOnly}
                        type="radio"
                        checked={whoDidYouReportThisTo === REPORT_TO.OTHER}
                        onChange={this.onChange(
                          'form.snowSportEquipment.whoDidYouReportThisTo'
                        )}
                        value={REPORT_TO.OTHER}
                      />
                      <FormattedMessage {...messages.other} />
                    </OptionsContainer>
                    <OptionsContainer>
                      <input
                        disabled={isReadOnly}
                        type="radio"
                        checked={whoDidYouReportThisTo === REPORT_TO.NOT_REPORT}
                        onChange={this.onChange(
                          'form.snowSportEquipment.whoDidYouReportThisTo'
                        )}
                        value={REPORT_TO.NOT_REPORT}
                      />
                      <FormattedMessage {...messages.notReported} />
                    </OptionsContainer>
                  </FlexBox>
                </FlexBox>
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.whoDidYouReportThisTo}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-md-6">
              <PageSectionHeader>
                <FormattedMessage {...messages.otherDescription} />
              </PageSectionHeader>
              <TextArea
                disabled={isReadOnly}
                name="otherDescription"
                type="text"
                onChange={this.handleChange(
                  'form.snowSportEquipment.otherDescription'
                )}
                value={otherDescription}
                isSummary={isSummary}
              />
            </div>
          </div>

          <div className="row">
            <div className="form-group col-sm-6">
              {whoDidYouReportThisTo !== REPORT_TO.NOT_REPORT && (
                <Validate
                  validatorLeaveGroup={
                    this.validatorGroup.removeValidatorToGroup
                  }
                  name="reportReferenceNumber"
                  validators={[required]}
                  group={this.validatorGroup}
                  addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                  validChangeInGroup={this.validatorGroup.validChangeInGroup}
                  validationCounter={this.state.validationCounter}
                  defaultValue={reportReferenceNumber}
                  fieldName={messages.reportReferenceNumber}
                >
                  <Textbox
                    disabled={isReadOnly}
                    name="reportReferenceNumber"
                    type="text"
                    onChange={this.handleChange(
                      'form.snowSportEquipment.reportReferenceNumber'
                    )}
                    value={reportReferenceNumber}
                  />
                  <ErrorMessage for="required">
                    <FormattedMessage
                      {...messages.isRequired}
                      values={messages.reportReferenceNumber}
                    />
                  </ErrorMessage>
                </Validate>
              )}
              {whoDidYouReportThisTo === REPORT_TO.NOT_REPORT && (
                <div>
                  <PageSectionHeader>
                    <FormattedMessage {...messages.reportReferenceNumber} />
                  </PageSectionHeader>
                  <Textbox
                    disabled={isReadOnly}
                    name="reportReferenceNumber"
                    type="text"
                    onChange={this.handleChange(
                      'form.snowSportEquipment.reportReferenceNumber'
                    )}
                    value={reportReferenceNumber}
                  />
                </div>
              )}
            </div>
          </div>

          {this.renderSnowSportEquipmentAdditionalItems()}
        </div>
      );
    }
    return null;
  }

  renderSnowEquipmentReplacementHireForm() {
    const { areYouClaimingForSnowEquipmentReplacementHire } = this.props.form;
    const {
      howItHappened,
      whereYouWereInRelationToTheItem,
      additional,
      claimingFor,
      whoDidYouReportThisTo,
      otherDescription,
      reportReferenceNumber,
    } = this.props.form.snowEquipmentReplacementHire;
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;

    if (areYouClaimingForSnowEquipmentReplacementHire === 'true') {
      return (
        <div className="mb-4">
          {this.renderClaimNoteForSnowSportEquipment()}
          <div className="row">
            <div className="col-12">
              <P style={{ fontStyle: 'bold' }}>
                <FormattedMessage
                  {...messages.ifYourSnowSportEquipmentsWasLostStolenOrDamagedPleaseTellUs}
                />
              </P>
            </div>
          </div>

          <div className="row">
            <div className="form-group col-12">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="howItHappened"
                validators={[required]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={howItHappened}
                fieldName={messages.howItHappened}
              >
                <TextArea
                  disabled={isReadOnly}
                  name="howItHappened"
                  type="text"
                  onChange={this.handleChange(
                    'form.snowEquipmentReplacementHire.howItHappened'
                  )}
                  value={howItHappened}
                  isSummary={isSummary}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.howItHappened}
                  />
                </ErrorMessage>
              </Validate>
            </div>
          </div>

          <div className="row">
            <div className="form-group col-12">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="whereYouWereInRelationToTheItem"
                validators={[required]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={whereYouWereInRelationToTheItem}
                fieldName={messages.whereYouWereInRelationToTheItem}
              >
                <TextArea
                  disabled={isReadOnly}
                  name="whereYouWereInRelationToTheItem"
                  type="text"
                  onChange={this.handleChange(
                    'form.snowEquipmentReplacementHire.whereYouWereInRelationToTheItem'
                  )}
                  value={whereYouWereInRelationToTheItem}
                  isSummary={isSummary}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.whereYouWereInRelationToTheItem}
                  />
                </ErrorMessage>
              </Validate>
            </div>
          </div>

          <div className="row">
            <div className="form-group col-12">
              <PageSectionHeader>
                <FormattedMessage {...messages.anyThingAdditionalToAdd} />
              </PageSectionHeader>
              <TextArea
                disabled={isReadOnly}
                name="additional"
                type="text"
                onChange={this.handleChange(
                  'form.snowEquipmentReplacementHire.additional'
                )}
                value={additional}
                isSummary={isSummary}
              />
            </div>
          </div>

          <div className="row">
            <div className="form-group col-12">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="areYouClaimingFor"
                validators={[required]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={groupOption(claimingFor)}
                fieldName={messages.areYouClaimingFor}
              >
                <FlexBox direction="column">
                  <OptionsContainer>
                    <input
                      disabled={isReadOnly}
                      type="checkbox"
                      name="loss"
                      checked={claimingFor.loss === true}
                      onChange={this.onChange(
                        'form.snowEquipmentReplacementHire.claimingFor.loss'
                      )}
                    />
                    <span>
                      <FormattedMessage {...messages.loss} />
                    </span>
                  </OptionsContainer>
                  <OptionsContainer>
                    <input
                      disabled={isReadOnly}
                      type="checkbox"
                      name="theft"
                      checked={claimingFor.theft === true}
                      onChange={this.onChange(
                        'form.snowEquipmentReplacementHire.claimingFor.theft'
                      )}
                    />
                    <span>
                      <FormattedMessage {...messages.theft} />
                    </span>
                  </OptionsContainer>
                  <OptionsContainer>
                    <input
                      disabled={isReadOnly}
                      type="checkbox"
                      name="damage"
                      checked={claimingFor.damage === true}
                      onChange={this.onChange(
                        'form.snowEquipmentReplacementHire.claimingFor.damage'
                      )}
                    />
                    <span>
                      <FormattedMessage {...messages.damage} />
                    </span>
                  </OptionsContainer>
                  <OptionsContainer>
                    <input
                      disabled={isReadOnly}
                      type="checkbox"
                      name="delay"
                      checked={claimingFor.delay === true}
                      onChange={this.onChange(
                        'form.snowEquipmentReplacementHire.claimingFor.delay'
                      )}
                    />
                    <span>
                      <FormattedMessage {...messages.delay} />
                    </span>
                  </OptionsContainer>
                </FlexBox>
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.areYouClaimingFor}
                  />
                </ErrorMessage>
              </Validate>
            </div>
          </div>

          <div className="row">
            <div className="form-group col-md-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="whoDidYouReportThisTo"
                validators={[required]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={whoDidYouReportThisTo}
                fieldName={messages.whoDidYouReportThisTo}
              >
                <FlexBox justifyContent="space-between">
                  <FlexBox direction="column">
                    <OptionsContainer>
                      <input
                        disabled={isReadOnly}
                        type="radio"
                        checked={whoDidYouReportThisTo === REPORT_TO.POLICE}
                        onChange={this.onChange(
                          'form.snowEquipmentReplacementHire.whoDidYouReportThisTo'
                        )}
                        value={REPORT_TO.POLICE}
                      />
                      <FormattedMessage {...messages.police} />
                    </OptionsContainer>
                    <OptionsContainer>
                      <input
                        disabled={isReadOnly}
                        type="radio"
                        checked={
                          whoDidYouReportThisTo === REPORT_TO.HOTEL_MANAGEMENT
                        }
                        onChange={this.onChange(
                          'form.snowEquipmentReplacementHire.whoDidYouReportThisTo'
                        )}
                        value={REPORT_TO.HOTEL_MANAGEMENT}
                      />
                      <FormattedMessage {...messages.hotelManagement} />
                    </OptionsContainer>
                    <OptionsContainer>
                      <input
                        disabled={isReadOnly}
                        type="radio"
                        checked={
                          whoDidYouReportThisTo === REPORT_TO.CRUISE_SHIP
                        }
                        onChange={this.onChange(
                          'form.snowEquipmentReplacementHire.whoDidYouReportThisTo'
                        )}
                        value={REPORT_TO.CRUISE_SHIP}
                      />
                      <FormattedMessage {...messages.cruiseShip} />
                    </OptionsContainer>
                    <OptionsContainer>
                      <input
                        disabled={isReadOnly}
                        type="radio"
                        checked={whoDidYouReportThisTo === REPORT_TO.AIRLINE}
                        onChange={this.onChange(
                          'form.snowEquipmentReplacementHire.whoDidYouReportThisTo'
                        )}
                        value={REPORT_TO.AIRLINE}
                      />
                      <FormattedMessage {...messages.airline} />
                    </OptionsContainer>
                  </FlexBox>
                  <FlexBox direction="column">
                    <OptionsContainer>
                      <input
                        disabled={isReadOnly}
                        type="radio"
                        checked={whoDidYouReportThisTo === REPORT_TO.TOUR_GUIDE}
                        onChange={this.onChange(
                          'form.snowEquipmentReplacementHire.whoDidYouReportThisTo'
                        )}
                        value={REPORT_TO.TOUR_GUIDE}
                      />
                      <FormattedMessage {...messages.tourGuide} />
                    </OptionsContainer>
                    <OptionsContainer>
                      <input
                        disabled={isReadOnly}
                        type="radio"
                        checked={whoDidYouReportThisTo === REPORT_TO.OTHER}
                        onChange={this.onChange(
                          'form.snowEquipmentReplacementHire.whoDidYouReportThisTo'
                        )}
                        value={REPORT_TO.OTHER}
                      />
                      <FormattedMessage {...messages.other} />
                    </OptionsContainer>
                    <OptionsContainer>
                      <input
                        disabled={isReadOnly}
                        type="radio"
                        checked={whoDidYouReportThisTo === REPORT_TO.NOT_REPORT}
                        onChange={this.onChange(
                          'form.snowEquipmentReplacementHire.whoDidYouReportThisTo'
                        )}
                        value={REPORT_TO.NOT_REPORT}
                      />
                      <FormattedMessage {...messages.notReported} />
                    </OptionsContainer>
                  </FlexBox>
                </FlexBox>
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.whoDidYouReportThisTo}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-md-6">
              <PageSectionHeader>
                <FormattedMessage {...messages.otherDescription} />
              </PageSectionHeader>
              <TextArea
                disabled={isReadOnly}
                name="otherDescription"
                type="text"
                onChange={this.handleChange(
                  'form.snowEquipmentReplacementHire.otherDescription'
                )}
                value={otherDescription}
                isSummary={isSummary}
              />
            </div>
          </div>

          <div className="row">
            <div className="form-group col-sm-6">
              {whoDidYouReportThisTo !== REPORT_TO.NOT_REPORT && (
                <Validate
                  validatorLeaveGroup={
                    this.validatorGroup.removeValidatorToGroup
                  }
                  name="reportReferenceNumber"
                  validators={[required]}
                  group={this.validatorGroup}
                  addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                  validChangeInGroup={this.validatorGroup.validChangeInGroup}
                  validationCounter={this.state.validationCounter}
                  defaultValue={reportReferenceNumber}
                  fieldName={messages.reportReferenceNumber}
                >
                  <Textbox
                    disabled={isReadOnly}
                    name="reportReferenceNumber"
                    type="text"
                    onChange={this.handleChange(
                      'form.snowEquipmentReplacementHire.reportReferenceNumber'
                    )}
                    value={reportReferenceNumber}
                  />
                  <ErrorMessage for="required">
                    <FormattedMessage
                      {...messages.isRequired}
                      values={messages.reportReferenceNumber}
                    />
                  </ErrorMessage>
                </Validate>
              )}
              {whoDidYouReportThisTo === REPORT_TO.NOT_REPORT && (
                <div>
                  <PageSectionHeader>
                    <FormattedMessage {...messages.reportReferenceNumber} />
                  </PageSectionHeader>
                  <Textbox
                    disabled={isReadOnly}
                    name="reportReferenceNumber"
                    type="text"
                    onChange={this.handleChange(
                      'form.snowEquipmentReplacementHire.reportReferenceNumber'
                    )}
                    value={reportReferenceNumber}
                  />
                </div>
              )}
            </div>
          </div>

          {this.renderSnowEquipmentReplacementHireAdditionalItems()}
        </div>
      );
    }
    return null;
  }

  renderPrepaidExpensesForm() {
    const { isSummary, submitted } = this.props;

    const {
      areYouClaimingForSnowPrepaidExpenses,
      patientName,
      relationshipToYou,
      patientDateOfBirth,
      medicareNumber,
      whatIsYourInjuryOrIllness,
      didThisOccurWhilstEngagingInSnowSportActivity,
      wasTheInjuredIllTravellerAnInpatient,
      areThereAnyAdditionalExpensesToBeClaimedSnowPrepaid,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    return (
      areYouClaimingForSnowPrepaidExpenses === 'true' && (
        <div className="mb-4">
          {this.renderClaimNoteForSnowRepaidExpenses()}

          <div className="row">
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="patientName"
                validators={[required]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={patientName}
                fieldName={messages.patientName}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="patientName"
                  type="text"
                  onChange={this.handleChange('form.patientName')}
                  value={patientName}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.patientName}
                  />
                </ErrorMessage>
              </Validate>
            </div>

            <div className="form-group col-sm-6">
              <PageSectionHeader>
                <FormattedMessage {...messages.relationshipToYou} />
              </PageSectionHeader>
              <Textbox
                disabled={isReadOnly}
                name="relationshipToYou"
                type="text"
                onChange={this.handleChange('form.relationshipToYou')}
                value={relationshipToYou}
              />
            </div>
          </div>

          <div className="row">
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="patientDateOfBirth"
                validators={[required]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={patientDateOfBirth}
                fieldName={messages.patientDateOfBirth}
              >
                <DatePicker
                  disabled={isReadOnly}
                  name="patientDateOfBirth"
                  type="text"
                  onDayChange={this.handleDayChange('form.patientDateOfBirth')}
                  value={patientDateOfBirth}
                  placeholder=""
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.patientDateOfBirth}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-6">
              <PageSectionHeader>
                <FormattedMessage {...messages.medicareNumber} />
              </PageSectionHeader>
              <Textbox
                disabled={isReadOnly}
                name="medicareNumber"
                type="text"
                onChange={this.handleChange('form.medicareNumber')}
                value={medicareNumber}
              />
            </div>
          </div>

          <div className="row">
            <div className="form-group col-12">
              <PageSectionHeader>
                <FormattedMessage {...messages.whatIsYourInjuryOrIllness} />
              </PageSectionHeader>
              <TextArea
                disabled={isReadOnly}
                name="whatIsYourInjuryOrIllness"
                type="text"
                onChange={this.handleChange('form.whatIsYourInjuryOrIllness')}
                value={whatIsYourInjuryOrIllness}
                isSummary={isSummary}
              />
            </div>
          </div>

          <div className="row">
            <div className="form-group col-sm-6">
              <PageSectionHeader>
                <FormattedMessage
                  {...messages.didThisOccurWhilstEngagingInSnowSportActivity}
                />
              </PageSectionHeader>
              <FlexBox>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={
                      didThisOccurWhilstEngagingInSnowSportActivity === 'true'
                    }
                    onChange={this.onChange(
                      'form.didThisOccurWhilstEngagingInSnowSportActivity'
                    )}
                    value="true"
                  />
                  <FormattedMessage {...messages.yes} />
                </OptionsContainer>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={
                      didThisOccurWhilstEngagingInSnowSportActivity === 'false'
                    }
                    onChange={this.onChange(
                      'form.didThisOccurWhilstEngagingInSnowSportActivity'
                    )}
                    value="false"
                  />
                  <FormattedMessage {...messages.no} />
                </OptionsContainer>
              </FlexBox>
            </div>

            <div className="form-group col-sm-6">
              <PageSectionHeader>
                <FormattedMessage
                  {...messages.wasTheInjuredIllTravellerAnInpatient}
                />
              </PageSectionHeader>
              <FlexBox>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={wasTheInjuredIllTravellerAnInpatient === 'true'}
                    onChange={this.onChange(
                      'form.wasTheInjuredIllTravellerAnInpatient'
                    )}
                    value="true"
                  />
                  <FormattedMessage {...messages.yes} />
                </OptionsContainer>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={wasTheInjuredIllTravellerAnInpatient === 'false'}
                    onChange={this.onChange(
                      'form.wasTheInjuredIllTravellerAnInpatient'
                    )}
                    value="false"
                  />
                  <FormattedMessage {...messages.no} />
                </OptionsContainer>
              </FlexBox>
            </div>
          </div>

          {this.renderCancellationExpenses('snowPrepaidCancellationExpenses')}

          <div className="row">
            <div className="form-group col">
              <PageSectionHeader>
                <FormattedMessage
                  {...messages.areThereAnyAdditionalExpensesToBeClaimed}
                />
              </PageSectionHeader>
              <FlexBox>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={
                      areThereAnyAdditionalExpensesToBeClaimedSnowPrepaid ===
                      'true'
                    }
                    onChange={this.onChange(
                      'form.areThereAnyAdditionalExpensesToBeClaimedSnowPrepaid'
                    )}
                    value="true"
                  />
                  <FormattedMessage {...messages.yes} />
                </OptionsContainer>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={
                      areThereAnyAdditionalExpensesToBeClaimedSnowPrepaid ===
                      'false'
                    }
                    onChange={this.onChange(
                      'form.areThereAnyAdditionalExpensesToBeClaimedSnowPrepaid'
                    )}
                    value="false"
                  />
                  <FormattedMessage {...messages.no} />
                </OptionsContainer>
              </FlexBox>
            </div>
          </div>
          {areThereAnyAdditionalExpensesToBeClaimedSnowPrepaid === 'true' &&
            this.renderAdditionalExpenses('snowPrepaidAdditionalExpenses')}
        </div>
      )
    );
  }

  renderSnowResortClosure() {
    const {
      areYouClaimingForSnowResortClosure,
      areThereAnyAdditionalExpensesToBeClaimedSnowResort,
    } = this.props.form;
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;
    return (
      areYouClaimingForSnowResortClosure === 'true' && (
        <div className="mb-4">
          {this.renderClaimNoteForSnowResortClosure()}
          {this.renderCancellationExpenses('snowResortCancellationExpenses')}
          <div className="row">
            <div className="form-group col">
              <PageSectionHeader>
                <FormattedMessage
                  {...messages.areThereAnyAdditionalExpensesToBeClaimed}
                />
              </PageSectionHeader>
              <FlexBox>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={
                      areThereAnyAdditionalExpensesToBeClaimedSnowResort ===
                      'true'
                    }
                    onChange={this.onChange(
                      'form.areThereAnyAdditionalExpensesToBeClaimedSnowResort'
                    )}
                    value="true"
                  />
                  <FormattedMessage {...messages.yes} />
                </OptionsContainer>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={
                      areThereAnyAdditionalExpensesToBeClaimedSnowResort ===
                      'false'
                    }
                    onChange={this.onChange(
                      'form.areThereAnyAdditionalExpensesToBeClaimedSnowResort'
                    )}
                    value="false"
                  />
                  <FormattedMessage {...messages.no} />
                </OptionsContainer>
              </FlexBox>
            </div>
          </div>
          {areThereAnyAdditionalExpensesToBeClaimedSnowResort === 'true' &&
            this.renderAdditionalExpenses('snowResortAdditionalExpenses')}
        </div>
      )
    );
  }

  renderSnowSportEquipmentAdditionalItems() {
    const { snowSportEquipmentAdditionalItems } = this.props.form;
    const newAdditionalItems = this.appendIds(
      snowSportEquipmentAdditionalItems
    );
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <PageSectionHeader>
          <FormattedMessage {...messages.addItem} />
        </PageSectionHeader>
        <AdditionalList
          data={newAdditionalItems}
          onChange={this.onAdditionalItemsChange(
            'snowSportEquipmentAdditionalItems'
          )}
          isDelayed={false}
          disabled={isReadOnly}
          validatorGroup={this.validatorGroup}
          validationCounter={this.state.validationCounter}
        />
      </div>
    );
  }

  renderSnowEquipmentReplacementHireAdditionalItems() {
    const { snowEquipmentReplacementHireAdditionalItems } = this.props.form;
    const newAdditionalItems = this.appendIds(
      snowEquipmentReplacementHireAdditionalItems
    );
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <PageSectionHeader>
          <FormattedMessage {...messages.addItem} />
        </PageSectionHeader>
        <AdditionalList
          data={newAdditionalItems}
          onChange={this.onAdditionalItemsChange(
            'snowEquipmentReplacementHireAdditionalItems'
          )}
          isDelayed={false}
          disabled={isReadOnly}
          validatorGroup={this.validatorGroup}
          validationCounter={this.state.validationCounter}
        />
      </div>
    );
  }

  renderCancellationExpenses(property) {
    const cancellationExpenses = this.props.form[property];
    const newCancellationExpenses = this.appendIds(cancellationExpenses);
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;
    return (
      <div>
        <PageSectionHeader>
          <FormattedMessage {...messages.cancellationExpenses} />
        </PageSectionHeader>
        <CancellationExpensesList
          data={newCancellationExpenses}
          onChange={this.onCancellationExpensesChange(property)}
          disabled={isReadOnly}
          validatorGroup={this.validatorGroup}
          validationCounter={this.state.validationCounter}
        />
      </div>
    );
  }

  renderAdditionalExpenses(property) {
    const additionalExpenses = this.props.form[property];
    const newAdditionalExpenses = this.appendIds(additionalExpenses);
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;
    return (
      <div>
        <PageSectionHeader>
          <FormattedMessage {...messages.additionalExpenses} />
        </PageSectionHeader>
        <FlexBox>
          <AdditionalExpensesList
            data={newAdditionalExpenses}
            onChange={this.onAdditionalExpensesChange(property)}
            disabled={isReadOnly}
            validatorGroup={this.validatorGroup}
            validationCounter={this.state.validationCounter}
          />
        </FlexBox>
      </div>
    );
  }

  renderSnowEquipmentReplacementHireOption() {
    const { areYouClaimingForSnowEquipmentReplacementHire } = this.props.form;
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      <div className="row">
        <div className="form-group col-12">
          <PageSectionHeader>
            <FormattedMessage
              {...messages.areYouClaimingForSnowEquipmentReplacementHire}
            />
          </PageSectionHeader>
          <FlexBox>
            <OptionsContainer>
              <input
                disabled={isReadOnly}
                type="radio"
                checked={
                  areYouClaimingForSnowEquipmentReplacementHire === 'true'
                }
                onChange={this.onChange(
                  'form.areYouClaimingForSnowEquipmentReplacementHire'
                )}
                value="true"
              />
              <FormattedMessage {...messages.yes} />
            </OptionsContainer>
            <OptionsContainer>
              <input
                disabled={isReadOnly}
                type="radio"
                checked={
                  areYouClaimingForSnowEquipmentReplacementHire === 'false'
                }
                onChange={this.onChange(
                  'form.areYouClaimingForSnowEquipmentReplacementHire'
                )}
                value="false"
              />
              <FormattedMessage {...messages.no} />
            </OptionsContainer>
          </FlexBox>
        </div>
      </div>
    );
  }

  renderClaimingOptions() {
    const {
      areYouClaimingForSnowSportsOptionalCover,
      areYouClaimingForSnowSportEquipment,
      areYouClaimingForSnowPrepaidExpenses,
      areYouClaimingForSnowResortClosure,
    } = this.props.form;
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      areYouClaimingForSnowSportsOptionalCover === 'true' && (
        <div className="ml-30">
          <div className="row">
            <div className="form-group col-12">
              <PageSectionHeader>
                <FormattedMessage
                  {...messages.areYouClaimingForSnowSportEquipment}
                />
              </PageSectionHeader>
              <FlexBox>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={areYouClaimingForSnowSportEquipment === 'true'}
                    onChange={this.onChange(
                      'form.areYouClaimingForSnowSportEquipment'
                    )}
                    value="true"
                  />
                  <FormattedMessage {...messages.yes} />
                </OptionsContainer>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={areYouClaimingForSnowSportEquipment === 'false'}
                    onChange={this.onChange(
                      'form.areYouClaimingForSnowSportEquipment'
                    )}
                    value="false"
                  />
                  <FormattedMessage {...messages.no} />
                </OptionsContainer>
              </FlexBox>
            </div>
          </div>

          {this.renderSnowSportEquipmentForm()}

          {this.renderSnowEquipmentReplacementHireOption()}

          {this.renderSnowEquipmentReplacementHireForm()}

          <div className="row">
            <div className="form-group col-12">
              <PageSectionHeader>
                <FormattedMessage
                  {...messages.areYouClaimingForSnowPrepaidExpenses}
                />
              </PageSectionHeader>
              <FlexBox>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={areYouClaimingForSnowPrepaidExpenses === 'true'}
                    onChange={this.onChange(
                      'form.areYouClaimingForSnowPrepaidExpenses'
                    )}
                    value="true"
                  />
                  <FormattedMessage {...messages.yes} />
                </OptionsContainer>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={areYouClaimingForSnowPrepaidExpenses === 'false'}
                    onChange={this.onChange(
                      'form.areYouClaimingForSnowPrepaidExpenses'
                    )}
                    value="false"
                  />
                  <FormattedMessage {...messages.no} />
                </OptionsContainer>
              </FlexBox>
            </div>
          </div>
          {this.renderPrepaidExpensesForm()}

          <div className="row">
            <div className="form-group col-12">
              <PageSectionHeader>
                <FormattedMessage
                  {...messages.areYouClaimingForSnowResortClosure}
                />
              </PageSectionHeader>
              <FlexBox>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={areYouClaimingForSnowResortClosure === 'true'}
                    onChange={this.onChange(
                      'form.areYouClaimingForSnowResortClosure'
                    )}
                    value="true"
                  />
                  <FormattedMessage {...messages.yes} />
                </OptionsContainer>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={areYouClaimingForSnowResortClosure === 'false'}
                    onChange={this.onChange(
                      'form.areYouClaimingForSnowResortClosure'
                    )}
                    value="false"
                  />
                  <FormattedMessage {...messages.no} />
                </OptionsContainer>
              </FlexBox>
            </div>
          </div>
          {this.renderSnowResortClosure()}
        </div>
      )
    );
  }

  render() {
    const { areYouClaimingForSnowSportsOptionalCover } = this.props.form;
    const { isSummary, submitted, loading } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      <div className="app-content">
        <div className="row">
          <div className="form-group col-12">
            <PageSectionHeader>
              <FormattedMessage
                {...messages.areYouClaimingForSnowSportsOptionalCover}
              />
            </PageSectionHeader>
            <FlexBox>
              <OptionsContainer>
                <input
                  disabled={isReadOnly}
                  type="radio"
                  checked={areYouClaimingForSnowSportsOptionalCover === 'true'}
                  onChange={this.onChange(
                    'form.areYouClaimingForSnowSportsOptionalCover'
                  )}
                  value="true"
                />
                <FormattedMessage {...messages.yes} />
              </OptionsContainer>
              <OptionsContainer>
                <input
                  disabled={isReadOnly}
                  type="radio"
                  checked={areYouClaimingForSnowSportsOptionalCover === 'false'}
                  onChange={this.onChange(
                    'form.areYouClaimingForSnowSportsOptionalCover'
                  )}
                  onClick={this.props.onReset}
                  value="false"
                />
                <FormattedMessage {...messages.no} />
              </OptionsContainer>
            </FlexBox>
          </div>
        </div>

        {this.renderClaimingOptions()}

        {!isSummary && (
          <div className={'row justify-content-between'}>
            <div className={'col-12 col-sm-auto'}>
              <div className={'row'}>
                <div className={'col-12 col-sm-auto'}>
                  <button
                    className="btn-outline"
                    onClick={this.props.prev}
                    onKeyDown={this.handleKeyDown}
                  >
                    Back
                  </button>
                </div>
                <div className={'col-12 col-sm-auto'}>
                  <Button
                    onClick={this.handleNext}
                    onKeyDown={this.handleKeyDown}
                    innerRef={(ref) => {
                      this.nextButton = ref;
                    }}
                  >
                    Next &gt;
                  </Button>
                </div>
              </div>
            </div>
            <div className={'col-12 col-sm-auto'}>
              {!submitted && (
                <AsyncButton
                  id={this.saveButtonId}
                  className="btn-outline"
                  loading={loading}
                  spinner={this.showSpinnerForElement(this.saveButtonId)}
                  onClick={this.handleSaveButtonClick}
                  onKeyDown={this.handleKeyDown}
                >
                  Save
                </AsyncButton>
              )}
            </div>
          </div>
        )}
        <ReactResizeDetector handleHeight onResize={this.onResize} />
      </div>
    );
  }
}

StepSnowSports.propTypes = {
  form: PropTypes.object,
  onFormDataChange: PropTypes.func,
  prev: PropTypes.func,
  next: PropTypes.func,
  onFormDataSave: PropTypes.func,
  onReset: PropTypes.func,
  loading: PropTypes.bool,
  isSummary: PropTypes.bool,
  submitted: PropTypes.bool,
  currentStep: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  step: PropTypes.number,
  isReadOnly: PropTypes.bool,
  errors: PropTypes.any,
  adjust: PropTypes.func,
  onNext: PropTypes.func,
  makeError: PropTypes.func,
  makeValid: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  form: makeSelectStepSnowSports(),
  errors: makeSelectStepSnowSportsErrors(),
  currentStep: makeSelectCurrentStep(),
  loading: makeSelectProgressBarStatus(),
});

const mapDispatchToProps = (dispatch) => ({
  onFormDataChange: (propertyName, value) =>
    dispatch(handleFormDataChange(propertyName, value)),
  onFormDataSave: () => dispatch(saveFormData()),
  onNext: (errors) => dispatch(nextSlide(errors)),
  onReset: () => dispatch(resetFormData()),
});

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps
);
const withReducer = injectReducer({
  key: 'stepSnowSports',
  reducer,
});

export default compose(
  withReducer,
  withConnect
)(StepSnowSports);



// WEBPACK FOOTER //
// ./app/containers/FastCover/StepSnowSports/index.js